import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiError, setStoredToken, getStoredToken, setActiveLocation } from "../lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = not authed, object = authed
  const [error, setError] = useState("");

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      try { localStorage.setItem("rougee:user", JSON.stringify(data)); } catch {}
    } catch {
      // If we have a stored token but /me fails, drop it.
      if (getStoredToken()) setStoredToken("");
      try { localStorage.removeItem("rougee:user"); } catch {}
      setUser(false);
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const login = async (email, password) => {
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.access_token) setStoredToken(data.access_token);
      setUser(data);
      try { localStorage.setItem("rougee:user", JSON.stringify(data)); } catch {}
      return true;
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || e.message;
      setError(msg);
      return false;
    }
  };

  const register = async (name, email, password, location) => {
    setError("");
    try {
      const { data } = await api.post("/auth/register", { name, email, password, location });
      if (data.access_token) setStoredToken(data.access_token);
      setUser(data);
      try { localStorage.setItem("rougee:user", JSON.stringify(data)); } catch {}
      return true;
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || e.message;
      setError(msg);
      return false;
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {}
    setStoredToken("");
    // Clear any global-admin location override so the next user starts clean.
    setActiveLocation("");
    try { localStorage.removeItem("rougee:user"); } catch {}
    setUser(false);
  };

  const role = (user && typeof user === "object" && user.role) || "viewer";
  const isAdmin = !!(user && (user.is_admin || role === "admin"));
  const isSupervisor = !!(user && (user.is_supervisor || role === "supervisor"));
  const isPrivileged = isAdmin || isSupervisor;
  const isRunnerLevel = isPrivileged || role === "runner";
  const isCrewLeader = role === "crew_leader";
  const isViewer = role === "viewer";
  const userLocation = (user && typeof user === "object" && user.location) || "";
  const isGlobalAdmin = !!(user && user.is_global_admin);

  return (
    <AuthCtx.Provider value={{
      user, error, login, register, logout, refresh: fetchMe,
      role, isAdmin, isSupervisor, isPrivileged, isRunnerLevel, isCrewLeader, isViewer,
      userLocation, isGlobalAdmin,
    }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
